import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { Organization, Period } from 'models/organization';
import { ClinicDropdown } from 'models/user';
import { getClinicsForLabTemplate } from 'apollo/templates/organization';

type CollectiveInvoicesPageActionProps = {
    onClinicPickChange: (clinic: ClinicDropdown) => void;
    onAllClinicsPickChange: (clinics: ClinicDropdown[], selectedPeriods: Period) => void;
    selectedClinics: { [key: string]: string };
    onPeriodChange: (selectedPeriods: Period, clinics: ClinicDropdown[]) => void;
};

const CollectiveInvoicesPageAction: FC<CollectiveInvoicesPageActionProps> = ({
    onClinicPickChange,
    onAllClinicsPickChange,
    selectedClinics,
    onPeriodChange,
}: CollectiveInvoicesPageActionProps) => {
    const { t: translate } = useTranslation(['orders', 'users', 'client-directory']);
    const [filteredClinics, setFilteredClinics] = useState<Organization[]>([]);
    const [clinicAutocompleteValue, setClinicAutocompleteValue] = useState('');
    const [selectedPeriods, setSelectedPeriods] = useState({
        monthly: true,
        semiWeekly: true,
        weekly: true,
    });
    const periodMap: Record<keyof typeof selectedPeriods, string> = {
        monthly: 'MONTHLY',
        semiWeekly: 'SEMI_WEEKLY',
        weekly: 'WEEKLY',
    };
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { clinicsForLab: clinics } = {}, loading } = useQuery(getClinicsForLabTemplate, {
        variables: {
            filters: { invoiceEnabled: true, isCollective: true, showParents: true, isPickUpAndDeliveryPlace: true },
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (clinics && selectedPeriods) {
            filteredClinicsList(selectedPeriods);
        }
    }, [clinics, selectedPeriods]);

    const filteredClinicsList = (selectedPeriods: Period) => {
        const allFalse = Object.values(selectedPeriods).every((value) => !value);
        if (allFalse) {
            setFilteredClinics(clinics);
            return;
        }
        const selectedEnumValues = Object.entries(selectedPeriods)
            .filter(([, isSelected]) => isSelected)
            .map(([key]) => periodMap[key as keyof typeof periodMap]);
        const filteredClinics = clinics.filter((clinic: Organization) =>
            selectedEnumValues.includes(clinic.invoicePeriod as string),
        );
        setFilteredClinics(filteredClinics);
    };

    const handleClinicSelect = (_: React.SyntheticEvent, clinic: ClinicDropdown | null) => {
        onClinicPickChange(clinic || ({} as ClinicDropdown));
    };

    const handlePeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
        const updatedPeriods = {
            ...selectedPeriods,
            [event.target.name]: event.target.checked,
        };
        setSelectedPeriods(updatedPeriods);
        onPeriodChange(updatedPeriods, clinics);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Grid item xs={12}>
            <Box mb={2}>
                <Box mb={1}>
                    <Typography variant="h4">{translate('client-directory:PERIOD')}</Typography>
                </Box>
                <FormControl component="fieldset" variant="standard">
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedPeriods.monthly}
                                    onChange={handlePeriodChange}
                                    name="monthly"
                                />
                            }
                            label={translate('client-directory:MONTHLY')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedPeriods.semiWeekly}
                                    onChange={handlePeriodChange}
                                    name="semiWeekly"
                                />
                            }
                            label={translate('client-directory:SEMI_WEEKLY')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedPeriods.weekly}
                                    onChange={handlePeriodChange}
                                    name="weekly"
                                />
                            }
                            label={translate('client-directory:WEEKLY')}
                        />
                    </FormGroup>
                </FormControl>
            </Box>
            <Box mb={2}>
                <Typography variant="h3">{translate('orders:CHOOSE_CLINICS_TO_INVOICE')}</Typography>
            </Box>
            <Box display="flex">
                <Box flexGrow={1} mr={1}>
                    <Autocomplete
                        options={filteredClinics as Organization[]}
                        inputValue={clinicAutocompleteValue}
                        onChange={handleClinicSelect}
                        getOptionLabel={(clinic) => clinic.name}
                        onClose={() => setClinicAutocompleteValue('')}
                        getOptionDisabled={(clinic) => !!selectedClinics[clinic._id]}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setClinicAutocompleteValue(event.target?.value)
                                }
                                label={translate('users:SELECT_CLINIC')}
                                placeholder={translate('SELECT_OR_SEARCH_CLINIC_FOR_INVOICE')}
                            />
                        )}
                    />
                </Box>
                <Box>
                    <Button
                        fullWidth
                        size="large"
                        onClick={() => {
                            onAllClinicsPickChange(filteredClinics, selectedPeriods);
                        }}
                    >
                        {translate('orders:SELECT_ALL_CLINICS')}
                    </Button>
                </Box>
            </Box>
        </Grid>
    );
};

export default CollectiveInvoicesPageAction;
